import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, finalize, map, mergeMap, shareReplay, take } from 'rxjs/operators';
import * as CalendarsPartnerActions from './calendars-partner.actions';
import { PartnerCommercialService } from 'src/app/pages/orders/services/partner-commercial.service';
import { selectCalendarsPartnerByKey } from './calendars-partner.selectors';
import { PartnerResponseCommercial } from 'src/app/pages/orders/interfaces/partnerCommercial.interface';

@Injectable()
export class CalendarsPartnerEffects {
  private pending: { [key: string]: any } = {};

  constructor(
    private actions$: Actions,
    private service: PartnerCommercialService,
    private store: Store,
  ) {}

  getCalendarsPartner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CalendarsPartnerActions.getCalendarsPartner),
      mergeMap((action) => {
        const key = action.key;

        if (this.pending[key]) {
          return of({ type: '[CalendarsPartner] Noop' });
        }

        return this.store.select(selectCalendarsPartnerByKey(key)).pipe(
          take(1),
          mergeMap((cached) => {
            if (cached !== null && !Array.isArray(cached)) {
              return of(
                CalendarsPartnerActions.getCalendarsPartnerSuccess({
                  key,
                  response: cached as PartnerResponseCommercial,
                }),
              );
            }

            if (cached === null) {
              this.store.dispatch(CalendarsPartnerActions.setCalendarsPartnerEmptyCache({ key, response: [] }));
            }

            const request$ = this.service
              .fetchCalendarsPartnerApi(
                action.params.countryCode,
                action.params.salesOrgCode,
                action.params.partnerCodes,
                action.params.cutOffTime,
              )
              .pipe(
                take(1),
                shareReplay(1),
                finalize(() => {
                  delete this.pending[key];
                }),
              );

            this.pending[key] = request$;

            return (request$ as unknown as import('rxjs').Observable<PartnerResponseCommercial>).pipe(
              map((response) => CalendarsPartnerActions.getCalendarsPartnerSuccess({ key, response })),
              catchError((error) => of(CalendarsPartnerActions.getCalendarsPartnerFailure({ key, error }))),
            );
          }),
        );
      }),
    ),
  );
}
